import { LinkIcon, XIcon } from '@phosphor-icons/react';
import {
  Button,
  Cell,
  Column,
  ConfirmModal,
  PageToolbar,
  Row,
  SearchField,
  TableFrame,
  TableHeader,
  Tooltip,
} from '@skupreme/shared/ui';
import useUrlPermission from '@hooks/url-permission';
import { CommonUtility } from '@utility/common';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import { useState } from 'react';
import { useUnmatchedAliases } from '../../hooks/use-provider-tabs';
import type { UnmatchedAlias } from '../../lib/missing-info';
import { PermissionGate } from '../PermissionGate';
import { MatchAliasModal } from './MatchAliasModal';

const COLUMN_COUNT = 3;

export type MissingInfoProvider = {
  _id?: string;
  marketplace_region_id?: { _id?: string };
};

/** Aliases the 3PL reports stock for that no catalog item holds yet. */
export function MissingInfoTab({
  provider,
}: {
  provider?: MissingInfoProvider;
}) {
  const providerId = provider?._id ?? '';
  const { disabled, hidden } = useUrlPermission();

  const [search, setSearch] = useState('');
  const [matching, setMatching] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const {
    loading,
    data,
    filter,
    pageChanged,
    filterChanged,
    refresh,
    totalData,
  } = useUnmatchedAliases(providerId, search);
  const aliases = (data ?? []) as UnmatchedAlias[];

  const run = async (action: () => Promise<unknown>, message: string) => {
    try {
      setProcessing(true);
      await action();
      ToastMessage.success(message);
      setMatching(null);
      setDismissing(null);
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const match = (itemId: string) =>
    matching &&
    run(
      () =>
        StorageProviderService.fetchSetSku(providerId, {
          alias: matching,
          item_id: itemId,
        }),
      '3PL Provided Alias matched with item successfully.',
    );

  const dismiss = () =>
    dismissing &&
    run(
      () => StorageProviderService.fetchRemoveSku(providerId, dismissing),
      'Successfully marked 3PL Provided Alias as not relevant',
    );

  return (
    <div className="sk:flex sk:flex-col">
      <PageToolbar className="sk:mx-0">
        <SearchField
          aria-label="Search aliases"
          placeholder="Search aliases..."
          value={search}
          onChange={setSearch}
          className="sk:w-64"
        />
      </PageToolbar>
      <TableFrame
        aria-label="Missing information"
        columnCount={COLUMN_COUNT}
        isLoading={loading}
        loadingRows={10}
        emptyMessage="Every alias this provider reports is matched to an item"
        pagination={{
          page: filter.page,
          pageSize: filter.limit,
          total: aliases.length ? totalData : 0,
          onPageChange: pageChanged,
          onPageSizeChange: size => filterChanged('limit', size),
        }}
        header={
          <TableHeader>
            <Column id="alias" isRowHeader>
              3PL Provided Alias
            </Column>
            <Column id="available" className="sk:w-40">
              Quantity
            </Column>
            <Column id="actions" aria-label="Actions" className="sk:w-40">
              {''}
            </Column>
          </TableHeader>
        }
      >
        {aliases.map(({ alias, available }) => (
          <Row key={alias} id={alias}>
            <Cell>{alias}</Cell>
            <Cell className="sk:tabular-nums">
              {CommonUtility.numberWithCommas(available || 0)}
            </Cell>
            <Cell>
              <PermissionGate disabled={disabled} hidden={hidden}>
                <div className="sk:flex sk:items-center sk:justify-end sk:gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    isDisabled={disabled || processing}
                    onPress={() => setMatching(alias)}
                  >
                    <LinkIcon size={12} weight="bold" aria-hidden />
                    Match
                  </Button>
                  <Tooltip content="Not relevant">
                    <Button
                      variant="ghost"
                      size="sm"
                      shape="circle"
                      aria-label="Mark as not relevant"
                      isDisabled={disabled || processing}
                      onPress={() => setDismissing(alias)}
                    >
                      <XIcon size={12} weight="bold" aria-hidden />
                    </Button>
                  </Tooltip>
                </div>
              </PermissionGate>
            </Cell>
          </Row>
        ))}
      </TableFrame>

      <MatchAliasModal
        alias={matching}
        regionId={provider?.marketplace_region_id?._id ?? ''}
        onClose={() => setMatching(null)}
        onMatch={match}
        isProcessing={processing}
      />
      <ConfirmModal
        isOpen={!!dismissing}
        title="Mark alias as not relevant"
        confirmLabel="Confirm"
        onClose={() => setDismissing(null)}
        onConfirm={dismiss}
      >
        The units of alias <b className="sk:text-fg">{dismissing}</b> will be
        treated as not relevant to this organization.
      </ConfirmModal>
    </div>
  );
}
